import { useState, type FormEvent } from 'react'
import PageSection from './components/PageSection'
import RouteLink from './components/RouteLink'
import CallToActionBand from './components/CallToActionBand'
import SiteVisual from './components/SiteVisual'
import { imageAssets } from './siteAssets'

export interface PageProps {
  onNavigate: (path: string) => void
}

interface DetailPageProps extends PageProps {
  badge: string
  title: string
  description: string
  problemTitle?: string
  problemCopy?: string
  whyItMatters?: string[]
  bullets: string[]
  outcomes?: string[]
}

function HeroActions({ onNavigate }: PageProps) {
  return (
    <div className="mt-8 flex flex-wrap gap-4">
      <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
        Book a Strategic Consultation
      </RouteLink>
      <RouteLink href="/platform" onNavigate={onNavigate} className="btn-secondary">
        Explore Rez-Hub Platform
      </RouteLink>
    </div>
  )
}

export function HomePage({ onNavigate }: PageProps) {
  return (
    <>
      <section id="main-content" className="section-shell pt-16" aria-labelledby="hero-heading">
        <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div className="max-w-3xl">
            <span className="section-badge" role="status" aria-label="Category">
              Indigenous-Owned Advisory & Implementation Firm
            </span>
            <h1 id="hero-heading" className="hero-title">
              Digital sovereignty<br />
              requires governance<br />
              and infrastructure<br />
              working together.
            </h1>
            <p className="hero-copy">
              Yethirihwanonhase is an Indigenous-owned firm that helps First Nations leadership
              and trusted partners align digital infrastructure with governance priorities.
              We deliver OCAP consulting, infrastructure advisory, AI readiness planning,
              and our flagship Rez-Hub platform.
            </p>
            <HeroActions onNavigate={onNavigate} />
            <div className="trust-strip" role="list" aria-label="Trust indicators">
              <span role="listitem">Indigenous-owned and partner-led</span>
              <span role="listitem">OCAP governance expertise</span>
              <span role="listitem">Serving First Nations, Indigenous orgs, and trusted partners</span>
              <span role="listitem">Advisory, implementation, and platform delivery</span>
            </div>
          </div>

          <div className="space-y-6">
            <SiteVisual asset={imageAssets.homeHero} aspectClassName="aspect-[4/5] md:aspect-[16/14]" />
            <div className="panel-surface p-8 md:p-10">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-copper-light">
                What We Deliver
              </p>
              <div className="mt-6 space-y-6">
                <div>
                  <p className="text-lg font-semibold text-birch">Governance-first digital infrastructure.</p>
                  <p className="mt-2 text-sm leading-6 text-weathered">
                    We start with governance requirements and stewardship responsibilities.
                    Infrastructure, platform, and AI decisions follow from that foundation.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2" role="list" aria-label="Service areas">
                  {[
                    'OCAP Governance Consulting',
                    'Infrastructure Advisory',
                    'AI Readiness & Policy',
                    'Rez-Hub Platform Implementation',
                  ].map((item) => (
                    <div key={item} className="capability-pill" role="listitem">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PageSection
        badge="Why This Matters"
        title="Most digital systems were not built for Indigenous governance."
        description="Standard platforms separate infrastructure, governance, and AI decisions into disconnected projects. Yethirihwanonhase integrates those layers so leadership can make digital decisions with clarity, control, and institutional accountability."
      >
        <div className="grid gap-5 md:grid-cols-3" role="list" aria-label="Core principles">
          {[
            'Infrastructure without governance is not sovereignty.',
            'Governance without operational systems does not hold.',
            'AI without policy creates avoidable risk.',
          ].map((statement) => (
            <div key={statement} className="card-panel" role="listitem">
              <p className="text-lg font-medium leading-7 text-birch">{statement}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Services"
        title="Advisory and implementation. Integrated by design."
        description="We don't separate strategy from execution. Whether you need OCAP consulting, infrastructure advisory, AI readiness planning, or platform deployment, we deliver solutions that work together."
      >
        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="grid gap-6 md:grid-cols-2">
            {[
              {
                title: 'OCAP Governance Consulting',
                body: 'Policy development, stewardship frameworks, and operational implementation support.',
              },
              {
                title: 'Infrastructure Advisory',
                body: 'Architecture design, hosting strategy, vendor assessment, and migration planning.',
              },
              {
                title: 'AI Readiness & Policy',
                body: 'Governance-first AI preparation, risk assessment, and responsible deployment planning.',
              },
              {
                title: 'Vendor Risk Assessment',
                body: 'Dependency mapping, procurement review, and digital risk analysis for leadership.',
              },
            ].map((item) => (
              <div key={item.title} className="card-panel">
                <p className="text-sm uppercase tracking-[0.22em] text-copper-light">{item.title}</p>
                <p className="mt-4 text-lg leading-7 text-weathered">{item.body}</p>
              </div>
            ))}
          </div>
          <SiteVisual asset={imageAssets.systemsDiagram} aspectClassName="aspect-[4/5] md:aspect-[4/3]" />
        </div>
      </PageSection>

      <PageSection
        badge="Rez-Hub Platform"
        title="Our flagship platform: Rez-Hub"
        description="Rez-Hub is a sovereign digital environment combining secure infrastructure, governed collaboration, and AI readiness. It's designed for organizations that need platform-level control, not just advisory services."
      >
        <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Platform components</p>
            <div className="mt-6 grid gap-4" role="list" aria-label="Platform components">
              {[
                'Rez-Hub Cloud — Sovereign infrastructure foundation',
                'Rez-Hub Chat — Secure messaging and collaboration',
                'Rez-Hub Meet — Video conferencing under your control',
                'Rez-Hub Mail — Email and calendar with governance',
              ].map((item) => (
                <div key={item} className="insight-card" role="listitem">
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Why organizations choose Rez-Hub</p>
            <div className="mt-6 space-y-6">
              {[
                {
                  title: 'Sovereignty-aligned architecture',
                  body: 'Infrastructure, governance, and AI readiness handled as connected decisions, not isolated projects.',
                },
                {
                  title: 'OCAP governance integration',
                  body: 'Data stewardship, access controls, and consent workflows built into the platform foundation.',
                },
                {
                  title: 'Indigenous-owned delivery',
                  body: 'Delivered by Yethirihwanonhase with advisory and implementation support included.',
                },
              ].map((item) => (
                <div key={item.title}>
                  <p className="text-lg font-medium text-birch">{item.title}</p>
                  <p className="mt-2 text-sm leading-6 text-weathered">{item.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <RouteLink href="/platform" onNavigate={onNavigate} className="btn-secondary">
                Explore Rez-Hub Platform
              </RouteLink>
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Who We Serve"
        title="Built for leadership teams carrying institutional responsibility."
        description="We work with organizations that need governance alignment, institutional discretion, and operational clarity—not off-the-shelf solutions."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[
            {
              title: 'First Nations leadership',
              body: 'For governing bodies making decisions about stewardship, data control, and modernization priorities.',
            },
            {
              title: 'Administration and operations',
              body: 'For teams managing collaboration, access, records, communications, and vendor relationships.',
            },
            {
              title: 'Indigenous organizations',
              body: 'For institutions and service providers that require higher-trust digital environments.',
            },
            {
              title: 'Trusted partners',
              body: 'For enterprise and public-sector teams working in Indigenous contexts where governance alignment matters.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[15rem]">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Outcomes"
        title="What changes after an engagement."
        description="The objective is not modernization for its own sake. It's stronger control, better governance alignment, and more disciplined long-term decision-making."
        actions={
          <>
            <RouteLink href="/services" onNavigate={onNavigate} className="btn-primary">
              Explore Services
            </RouteLink>
            <RouteLink href="/platform" onNavigate={onNavigate} className="btn-secondary">
              Explore Rez-Hub Platform
            </RouteLink>
          </>
        }
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[
            'Clearer authority over systems and data',
            'Better alignment between governance and operations',
            'Reduced dependency on misaligned vendors',
            'Greater readiness for responsible AI',
          ].map((item) => (
            <div key={item} className="card-panel min-h-[11rem]">
              <p className="text-lg leading-7 text-birch">{item}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Methodology"
        title="Governance before technology. Capability before dependency."
        description="Our approach is designed to strengthen internal ownership, not create perpetual consulting relationships."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Four-phase engagement model</p>
            <div className="mt-6 space-y-5">
              {[
                {
                  phase: 'Discovery',
                  body: 'Current-state assessment, governance gap analysis, vendor dependency mapping.',
                },
                {
                  phase: 'Design',
                  body: 'Governance framework, infrastructure architecture, implementation roadmap.',
                },
                {
                  phase: 'Implementation',
                  body: 'Advisory delivery, platform deployment, governance integration, team training.',
                },
                {
                  phase: 'Transition',
                  body: 'Knowledge transfer, operating documentation, internal capability building.',
                },
              ].map((item, index) => (
                <div key={item.phase} className="flex gap-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-copper/30 bg-copper/10 text-sm font-semibold text-copper-light">
                    {index + 1}
                  </div>
                  <div className="pt-1">
                    <p className="text-base font-medium text-birch">{item.phase}</p>
                    <p className="mt-1 text-sm leading-6 text-weathered">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What makes this different</p>
            <div className="mt-6 space-y-6">
              {[
                {
                  title: 'Indigenous-owned and partner-led',
                  body: 'We understand sovereignty as lived experience, not academic theory. Our approach is grounded in Indigenous governance principles and operational realities.',
                },
                {
                  title: 'No generic packages',
                  body: 'Every engagement is scoped based on actual governance context, not forced into predefined service tiers or platform bundles.',
                },
                {
                  title: 'Governance shapes technology',
                  body: 'We start with stewardship requirements and control boundaries. Platform and infrastructure decisions follow from that foundation.',
                },
              ].map((item) => (
                <div key={item.title}>
                  <p className="text-base font-medium text-birch">{item.title}</p>
                  <p className="mt-2 text-sm leading-6 text-weathered">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Proof"
        title="Built for high-trust institutional work."
        description="We work with organizations that require discretion, governance alignment, and long-term stewardship discipline."
      >
        <div className="grid gap-6 lg:grid-cols-3">
          {[
            {
              title: 'Sectors served',
              items: [
                'First Nations governance and administration',
                'Indigenous service organizations',
                'Public sector agencies in Indigenous contexts',
                'Enterprise partners in high-trust relationships',
              ],
            },
            {
              title: 'Engagement types',
              items: [
                'OCAP governance consulting and implementation',
                'Infrastructure architecture and advisory',
                'AI readiness assessments and policy development',
                'Rez-Hub platform deployment and training',
              ],
            },
            {
              title: 'Operating principles',
              items: [
                'Confidentiality and institutional discretion',
                'Relationship protocols and cultural respect',
                'Grounded language, no transformation hype',
                'Internal capability building, not dependency',
              ],
            },
          ].map((item) => (
            <div key={item.title} className="card-panel">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">{item.title}</p>
              <ul className="mt-5 space-y-3 text-sm leading-6 text-weathered">
                {item.items.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </PageSection>

      <CallToActionBand
        badge="Get Started"
        title="Start with the decision in front of you."
        description="Whether you need OCAP governance consulting, infrastructure advisory, AI readiness planning, or Rez-Hub platform deployment, we can start with a focused consultation."
        actions={
          <>
            <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
              Book a Strategic Consultation
            </RouteLink>
            <RouteLink href="/services" onNavigate={onNavigate} className="btn-secondary">
              Explore Services
            </RouteLink>
          </>
        }
      />
    </>
  )
}

export function PlatformPage({ onNavigate }: PageProps) {
  return (
    <>
      <PageSection
        badge="Rez-Hub Platform"
        title="Our flagship platform: Rez-Hub."
        description="Rez-Hub is a sovereign digital environment combining secure infrastructure, governed collaboration, and AI readiness. Delivered by Yethirihwanonhase."
        actions={
          <>
            <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
              Request a Platform Consultation
            </RouteLink>
            <a href="https://rez-hub.com" target="_blank" rel="noopener noreferrer" className="btn-secondary">
              Visit Rez-Hub.com
            </a>
          </>
        }
        className="pt-16"
      >
        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="card-panel">
            <p className="text-lg leading-8 text-weathered">
              Rez-Hub is not just software. It's a complete sovereign digital environment
              that combines infrastructure, governance, collaboration, and AI readiness.
              It's designed for Indigenous organizations and trusted partners that need
              platform-level control, not just advisory services.
            </p>
            <p className="mt-6 text-lg leading-8 text-weathered">
              For full platform details, technical architecture, pricing, and documentation,
              visit <a href="https://rez-hub.com" target="_blank" rel="noopener noreferrer" className="text-copper-light hover:text-copper transition-colors">rez-hub.com</a>.
            </p>
          </div>
          <div className="space-y-6">
            <SiteVisual asset={imageAssets.platformHero} aspectClassName="aspect-[4/3]" />
            <div className="panel-surface p-8">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Platform positioning</p>
              <p className="mt-5 text-sm leading-6 text-weathered">
                Rez-Hub is one offering within Yethirihwanonhase's portfolio. Organizations
                can engage with us for advisory services only, platform implementation, or
                a hybrid approach.
              </p>
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Platform Components"
        title="Everything your organization needs. Nothing you don't control."
        description="Rez-Hub replaces fragmented, foreign-owned tools with a unified, sovereign cloud suite."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {[
            {
              title: 'Rez-Hub Cloud',
              body: 'Sovereign infrastructure foundation with data residency control.',
            },
            {
              title: 'Rez-Hub Chat',
              body: 'Secure messaging and collaboration for high-trust communications.',
            },
            {
              title: 'Rez-Hub Meet',
              body: 'Video conferencing under your control with consent-based recording.',
            },
            {
              title: 'Rez-Hub Mail',
              body: 'Email and calendar with governance controls and data stewardship.',
            },
            {
              title: 'Rez-Hub Circle',
              body: 'Community and collaboration platform with OCAP-aligned access.',
            },
            {
              title: 'Implementation Support',
              body: 'Advisory, deployment, training, and governance integration included.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Why Rez-Hub"
        title="Built for control, not convenience."
        description="Standard platforms optimize for vendor profit, not Indigenous governance. Rez-Hub is designed to give organizations stronger control over their digital infrastructure."
      >
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: 'Sovereignty-aligned architecture',
              body: 'Infrastructure, governance, and AI readiness designed as connected decisions.',
            },
            {
              title: 'OCAP governance integration',
              body: 'Data stewardship, access controls, and consent workflows built into the foundation.',
            },
            {
              title: 'Indigenous-owned delivery',
              body: 'Delivered by Yethirihwanonhase with full advisory and implementation support.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Implementation"
        title="From assessment to deployment in a structured program."
        description="Rez-Hub implementation includes advisory support, governance integration, and capability building."
      >
        <div className="grid gap-5 md:grid-cols-5">
          {[
            'Assess current state and governance needs',
            'Design architecture and governance framework',
            'Deploy platform and migrate systems',
            'Train teams and build internal capability',
            'Ongoing support and optimization',
          ].map((step, index) => (
            <div key={step} className="card-panel">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">
                Phase {index + 1}
              </p>
              <p className="mt-4 text-base leading-6 text-birch">{step}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <CallToActionBand
        badge="Learn More"
        title="Explore the full Rez-Hub platform."
        description="For detailed platform information, technical architecture, pricing, security documentation, and implementation guides, visit the Rez-Hub platform site."
        actions={
          <>
            <a href="https://rez-hub.com" target="_blank" rel="noopener noreferrer" className="btn-primary">
              Visit Rez-Hub.com
            </a>
            <RouteLink href="/contact" onNavigate={onNavigate} className="btn-secondary">
              Request a Platform Consultation
            </RouteLink>
          </>
        }
      />
    </>
  )
}

export function SolutionsPage({ onNavigate }: PageProps) {
  return (
    <>
      <PageSection
        badge="Services"
        title="Governance, infrastructure, AI readiness, and platform implementation."
        description="We deliver advisory services, platform deployment, or integrated programs. Every engagement is scoped to actual governance context and organizational priorities—not forced into predefined packages."
        className="pt-16"
      >
        <div className="grid gap-6 md:grid-cols-2">
          {[
            {
              title: 'OCAP Governance Consulting',
              body: 'Turn stewardship principles into operational controls. Includes data governance frameworks, consent workflow design, access policy development, and stewardship role definition. Delivered standalone or integrated with infrastructure implementation.',
              path: '/services/governance-ocap',
            },
            {
              title: 'Infrastructure Advisory',
              body: 'Sovereign infrastructure design and vendor risk assessment. Includes architecture blueprints, data residency planning, hosting strategy, vendor lock-in analysis, and migration roadmaps. Advisory-only or with Rez-Hub deployment.',
              path: '/services/infrastructure',
            },
            {
              title: 'AI Readiness & Policy Development',
              body: 'Governance-first AI preparation. Includes readiness assessments, use-case screening, AI governance policy frameworks, data-use risk analysis, and deployment pathway recommendations. Helps leadership make disciplined AI decisions.',
              path: '/services/ai-readiness',
            },
            {
              title: 'Vendor Risk Assessment',
              body: 'Digital dependency mapping and procurement review. Includes vendor lock-in analysis, data flow mapping, contract-governance misalignment review, privacy exposure assessment, and decision-ready recommendations for renewals or transitions.',
              path: '/services/risk-advisory',
            },
            {
              title: 'Rez-Hub Platform Implementation',
              body: 'Our flagship sovereign digital environment. Complete platform deployment with infrastructure design, governance integration, team training, and ongoing support.',
              path: '/platform',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[15rem]">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">{item.title}</p>
              <p className="mt-4 text-base leading-7 text-weathered">{item.body}</p>
              <RouteLink href={item.path} onNavigate={onNavigate} className="btn-tertiary mt-8">
                Learn More
              </RouteLink>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Engagement Models"
        title="Advisory, implementation, or integrated programs."
        description="We don't force organizations into rigid service tiers. The engagement model adapts to governance priorities, leadership capacity, and operational readiness."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      >
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: 'Advisory Only',
              body: 'Standalone consulting without platform deployment. OCAP governance, infrastructure architecture, AI readiness assessments, or vendor risk reviews. Deliverables: frameworks, roadmaps, recommendations.',
              examples: 'OCAP policy development, infrastructure planning, AI governance frameworks.',
            },
            {
              title: 'Platform Implementation',
              body: 'Rez-Hub deployment with full advisory integration. Infrastructure design, governance frameworks, platform deployment, team training, and transition support. Deliverables: deployed platform, governance controls, trained teams.',
              examples: 'Rez-Hub Cloud deployment, sovereign collaboration environment, governed digital infrastructure.',
            },
            {
              title: 'Hybrid Engagement',
              body: 'Custom combination of advisory and implementation work. Phased approach based on governance readiness and leadership priorities. Advisory first, platform later, or concurrent delivery.',
              examples: 'OCAP consulting + infrastructure advisory, AI readiness + platform scoping, phased Rez-Hub deployment.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[18rem]">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
              <p className="mt-4 text-xs uppercase tracking-wider text-copper-light">Examples</p>
              <p className="mt-2 text-sm leading-6 text-weathered">{item.examples}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="How We Work"
        title="Start with the decision in front of you."
        description="We don't force organizations into predefined packages. We start with your immediate challenge and build the right scope from there."
      >
        <div className="grid gap-6 lg:grid-cols-3">
          {[
            {
              title: 'Start with governance',
              body: 'Use this path when policy, stewardship, access, or OCAP implementation is the immediate concern.',
              cta: '/solutions/governance-ocap',
            },
            {
              title: 'Start with infrastructure',
              body: 'Use this path when the core issue is hosting, collaboration, access control, migration, or vendor dependence.',
              cta: '/solutions/infrastructure',
            },
            {
              title: 'Start with readiness',
              body: 'Use this path when leadership needs risk assessment, AI readiness review, or decision-support consultation before committing to a larger scope.',
              cta: '/contact',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
              <RouteLink href={item.cta} onNavigate={onNavigate} className="btn-tertiary mt-8">
                Continue
              </RouteLink>
            </div>
          ))}
        </div>
      </PageSection>

      <CallToActionBand
        badge="Get Started"
        title="Ready to define the right scope?"
        description="We can begin with a strategic consultation and direct you to the right solution path without forcing a larger scope too early."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      />
    </>
  )
}

export function DetailPage({
  onNavigate,
  badge,
  title,
  description,
  problemTitle,
  problemCopy,
  whyItMatters,
  bullets,
  outcomes,
}: DetailPageProps) {
  return (
    <>
      <PageSection
        badge={badge}
        title={title}
        description={description}
        className="pt-16"
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      >
        {problemTitle && problemCopy ? (
          <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="panel-surface p-8">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">The problem</p>
              <p className="mt-4 text-2xl font-display leading-tight text-birch">{problemTitle}</p>
            </div>
            <div className="card-panel">
              <p className="text-lg leading-8 text-weathered">{problemCopy}</p>
            </div>
          </div>
        ) : null}
      </PageSection>

      <PageSection
        badge="Why this matters"
        title="Designed to turn risk into a workable operating path"
        description="These engagements are structured to give leadership a clearer basis for action, not just another report or abstract recommendation."
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {(whyItMatters ?? [
            'Clearer decision-making for leadership and operations.',
            'More visible control points across systems and access.',
            'A stronger basis for procurement, governance, and modernization choices.',
            'Reduced drift between policy intent and operational reality.',
          ]).map((item) => (
            <div key={item} className="card-panel">
              <p className="text-base leading-7 text-birch">{item}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="What this includes"
        title="Structured scope with practical deliverables"
        description="The engagement is designed to produce decisions, controls, and operating structure rather than vague recommendations."
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {bullets.map((item) => (
            <div key={item} className="card-panel">
              <p className="text-base leading-7 text-birch">{item}</p>
            </div>
          ))}
        </div>
      </PageSection>

      {outcomes ? (
        <PageSection
          badge="Outcomes"
          title="What leadership gains"
          description="The objective is better control, clearer accountability, and more durable internal capability."
        >
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {outcomes.map((item) => (
              <div key={item} className="card-panel">
                <p className="text-base leading-7 text-birch">{item}</p>
              </div>
            ))}
          </div>
        </PageSection>
      ) : null}

      <PageSection
        badge="Engagement path"
        title="A practical way to start"
        description="Most organizations do not need to define the full end state on day one. The better approach is to start with the immediate issue, clarify requirements, and scope from there."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      >
        <div className="grid gap-4 md:grid-cols-4">
          {[
            'Clarify the current issue, pressure, or decision.',
            'Review the governance and operating constraints.',
            'Define the most useful scope and sequence.',
            'Move into implementation with a clearer base.',
          ].map((item, index) => (
            <div key={item} className="card-panel">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Step {index + 1}</p>
              <p className="mt-4 text-base leading-7 text-birch">{item}</p>
            </div>
          ))}
        </div>
      </PageSection>
    </>
  )
}

export function AboutPage({ onNavigate }: PageProps) {
  return (
    <>
      <PageSection
        badge="About Yethirihwanonhase"
        title="Digital systems that match the weight of the decisions they support."
        description="Yethirihwanonhase is an Indigenous-owned digital infrastructure and advisory firm focused on sovereignty, governance, and responsible modernization."
        className="pt-16"
      >
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="card-panel">
            <p className="text-lg leading-8 text-weathered">
              Too many organizations are asked to modernize through systems that reduce control, blur accountability, or ignore Indigenous governance realities.
            </p>
            <p className="mt-6 text-lg leading-8 text-weathered">
              Yethirihwanonhase was built to offer a more grounded path: digital systems that respect stewardship, institutional trust, and long-term autonomy.
            </p>
          </div>
          <div className="space-y-6">
            <div className="panel-surface p-8 md:p-10">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What we believe</p>
              <div className="mt-5 space-y-4 text-sm leading-6 text-weathered">
                <p>Governance must shape technology decisions.</p>
                <p>Infrastructure choices are leadership choices.</p>
                <p>Data stewardship is operational, not symbolic.</p>
                <p>AI adoption must be earned through policy and control.</p>
              </div>
            </div>
            <div className="panel-surface p-8">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">How we work</p>
              <div className="mt-5 space-y-4 text-sm leading-6 text-weathered">
                <p>Advisory and implementation are connected.</p>
                <p>We prioritize clarity over hype.</p>
                <p>We design for long-term internal capability.</p>
                <p>We work in ways suited to high-trust environments.</p>
              </div>
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Who We Serve"
        title="Built for organizations carrying institutional responsibility."
        description="We work with leadership teams that need digital decisions aligned with governance priorities, not the other way around."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[
            {
              title: 'First Nations Leadership',
              body: 'Chief and Council, governance bodies, and administration teams making decisions about data stewardship, infrastructure control, and modernization priorities.',
            },
            {
              title: 'Indigenous Organizations',
              body: 'Service providers, institutions, and community organizations that require higher-trust digital environments and OCAP-aligned systems.',
            },
            {
              title: 'Public Sector Partners',
              body: 'Government agencies and public institutions working in Indigenous contexts where governance alignment and relationship protocols matter.',
            },
            {
              title: 'Enterprise Partners',
              body: 'Private sector organizations operating in high-trust Indigenous partnerships where discretion and governance fit are non-negotiable.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[16rem]">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Operating Philosophy"
        title="How we think about the work."
        description="These principles shape how we engage, what we deliver, and why organizations continue working with us beyond the initial project."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Governance before technology</p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              We don't start with platform selection. We start with governance requirements, stewardship responsibilities, and control boundaries. Technology choices follow from that foundation.
            </p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              This approach takes longer upfront but produces systems that leadership can actually govern, not just operate.
            </p>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Build internal capability, not dependency</p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              The objective is not perpetual consulting relationships. It's stronger internal ownership over time.
            </p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              We design engagements to transfer knowledge, build team capacity, and establish operating structures that don't require us to maintain.
            </p>
          </div>
        </div>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {[
            {
              title: 'No generic packages',
              body: 'Every engagement is scoped based on actual governance context, not forced into predefined service tiers.',
            },
            {
              title: 'Grounded language',
              body: 'We avoid transformation hype, innovation theater, and buzzword-heavy positioning. The work speaks through structure and outcomes.',
            },
            {
              title: 'Institutional discretion',
              body: 'High-trust work requires confidentiality, relationship protocols, and an understanding that not everything is a case study.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[13rem]">
              <p className="text-base font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Engagement Model"
        title="How engagements typically unfold."
        description="We don't force organizations into rigid methodologies. The engagement sequence adapts to governance context, leadership priorities, and operational realities."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Discovery & Assessment</p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              We start with a strategic consultation or readiness assessment to understand current systems, governance gaps, vendor dependencies, and leadership priorities.
            </p>
            <p className="mt-5 text-sm leading-6 text-weathered">
              Typical duration: 2-4 weeks. Deliverable: Current-state assessment with governance gap analysis and recommended scope.
            </p>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Design & Planning</p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              We develop the governance framework, infrastructure architecture, and implementation roadmap based on actual control requirements, not generic best practices.
            </p>
            <p className="mt-5 text-sm leading-6 text-weathered">
              Typical duration: 4-8 weeks. Deliverable: Governance framework, architecture blueprint, and phased implementation plan.
            </p>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Implementation & Deployment</p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              We execute the implementation program—whether advisory-only, platform deployment, or hybrid—with governance integration and capability building throughout.
            </p>
            <p className="mt-5 text-sm leading-6 text-weathered">
              Typical duration: 8-16 weeks depending on scope. Deliverable: Deployed systems, governance controls, and trained internal teams.
            </p>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Transition & Sustainment</p>
            <p className="mt-5 text-lg leading-8 text-weathered">
              We transfer operational ownership to internal teams with documentation, training, and support structures that reduce long-term external dependency.
            </p>
            <p className="mt-5 text-sm leading-6 text-weathered">
              Typical duration: 4-6 weeks. Deliverable: Operating documentation, trained staff, and ongoing support plan.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Leadership"
        title="Partner-led firm."
        description="Yethirihwanonhase is led by four partners: Carmen Miller, Jesee Williams, Keaton Williams, and Caroline Van Every-Albert."
        actions={
          <RouteLink href="/bios" onNavigate={onNavigate} className="btn-secondary">
            Meet the Leadership Team
          </RouteLink>
        }
      />

      <CallToActionBand
        badge="Work With Us"
        title="Start with a strategic consultation."
        description="Whether you need OCAP governance consulting, infrastructure advisory, AI readiness planning, or Rez-Hub platform deployment, we can start with a focused discussion about your immediate priorities."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      />
    </>
  )
}

export function BiosPage({ onNavigate }: PageProps) {
  const leadershipProfiles = [
    {
      name: 'Carmen Miller',
      role: 'Partner, Sovereign Systems & Strategy',
      image: imageAssets.carmenPortrait,
      aspect: 'aspect-[4/5]',
      bio: "Carmen Miller is a Partner at Yethirihwanonhase, supporting the firm's work across sovereign systems, strategy, and implementation. His focus is on helping build practical, grounded structures that strengthen readiness, improve operations, and support long-term organizational capability. He brings an execution-driven perspective to governance-aligned modernization and systems that are designed to protect and serve communities.",
      linkedin: 'https://www.linkedin.com/in/carmenmiller-sovereignty/',
      focusAreas: [
        'Sovereign systems and strategy',
        'Digital strategy and implementation',
        'Governance-aligned modernization',
        'Operational readiness and systems design',
        'Community-centered infrastructure thinking',
        'Long-term capability building',
        'Rez-Hub strategy and deployment support',
      ],
    },
    {
      name: 'Jesee Williams',
      role: 'Partner',
      image: imageAssets.jeseePortrait,
      aspect: 'aspect-[4/5]',
      bio: "Jesee Williams is a Partner at Yethirihwanonhase. As a professional accountant, she brings financial expertise and strategic insight to the firm. She helps maintain strong fiscal discipline while supporting the administrative and operational functions that keep the business running effectively. Her precision and reliability are important to the company's stability and growth.",
    },
    {
      name: 'Keaton Williams',
      role: 'Partner, Digital Strategy & Implementation',
      image: imageAssets.keatonPortrait,
      aspect: 'aspect-[4/5]',
      bio: "Keaton Williams is a Partner at Yethirihwanonhase, supporting digital strategy, implementation, and platform development initiatives. He brings experience from launching and operating ventures across sales, digital advertising, media, and service-based businesses. His approach focuses on execution, clarity, and building systems that create measurable, long-term value within Indigenous communities.",
      linkedin: 'https://www.linkedin.com/in/keaton-williams-ab182aa7/',
      focusAreas: [
        'Digital strategy and implementation',
        'Marketing systems and audience development',
        'Operational workflow design and process improvement',
        'Community-based business development',
        'Rez-Hub platform development and deployment support',
        'Content, media, and communication strategy',
      ],
    },
    {
      name: 'Caroline VanEvery-Albert',
      role: 'Partner',
      image: imageAssets.carolinePortrait,
      aspect: 'aspect-[4/5]',
      bio: "Caroline VanEvery-Albert is a Partner at Yethirihwanonhase. As an educator, she brings a grounded and community-centered perspective to the firm's work, supporting practical approaches to long-term capability and organizational strength.",
      linkedin: 'https://www.linkedin.com/in/caroline-vanevery-albert-5009b11/',
    },
  ]

  return (
    <>
      <PageSection
        badge="Leadership"
        title="Partner-led firm."
        description="Yethirihwanonhase is led by four partners bringing complementary expertise across governance, infrastructure, AI readiness, and implementation."
        className="pt-16"
      >
        <div className="grid gap-6 md:grid-cols-2">
          {leadershipProfiles.map((profile) => (
            <article key={profile.name} className={profile.bio ? 'leadership-card' : 'card-panel min-h-[20rem] flex flex-col'}>
              <SiteVisual asset={profile.image} aspectClassName={profile.aspect} className="rounded-none border-0 bg-transparent shadow-none" />
              <div className={profile.bio ? 'leadership-card-copy' : 'mt-6'}>
                <p className={profile.bio ? 'text-sm uppercase tracking-[0.22em] text-copper-light' : 'text-lg font-medium text-birch'}>{profile.name}</p>
                <p className={profile.bio ? 'mt-3 text-xl leading-8 text-birch' : 'mt-2 text-sm uppercase tracking-[0.22em] text-copper-light'}>{profile.role}</p>
                {profile.bio && (
                  <p className="mt-5 text-base leading-7 text-weathered">{profile.bio}</p>
                )}
                {profile.focusAreas && (
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.22em] text-copper-light">Focus Areas</p>
                    <ul className="mt-3 space-y-2">
                      {profile.focusAreas.map((area) => (
                        <li key={area} className="text-sm leading-6 text-weathered">
                          {area}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {profile.linkedin && (
                  <div className="mt-6">
                    <a
                      href={profile.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-copper-light hover:text-copper transition-colors"
                    >
                      LinkedIn Profile →
                    </a>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Approach"
        title="Partner-led delivery across the full engagement lifecycle."
        description="The partner team works directly with clients on governance, infrastructure, AI readiness, and implementation. Engagements are scoped based on actual governance context and organizational priorities."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Integrated solutions</p>
            <p className="mt-5 text-base leading-7 text-weathered">
              Yethirihwanonhase operates as a partner-led firm delivering integrated advisory and implementation services across governance, infrastructure, AI readiness, and platform deployment.
            </p>
            <p className="mt-5 text-base leading-7 text-weathered">
              This structure allows us to address both strategic governance requirements and practical implementation realities without forcing organizations into predefined service packages.
            </p>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Direct engagement</p>
            <p className="mt-5 text-base leading-7 text-weathered">
              Partners work directly with client leadership teams to assess current state, design governance-aligned solutions, and guide implementation from discovery through transition.
            </p>
            <p className="mt-5 text-base leading-7 text-weathered">
              Every engagement is designed to build internal capability and reduce long-term external dependency.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Service Coverage"
        title="Expertise across the full engagement lifecycle."
        description="From initial governance assessment through platform deployment and capability building, the partner team covers all phases of digital modernization."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[
            {
              title: 'Governance & OCAP',
              body: 'Data stewardship frameworks, policy development, consent workflows, and OCAP-aligned system design.',
            },
            {
              title: 'Infrastructure & Security',
              body: 'Architecture design, hosting strategy, vendor assessment, migration planning, and deployment support.',
            },
            {
              title: 'AI Readiness & Policy',
              body: 'Readiness assessments, governance frameworks, use-case screening, and responsible deployment planning.',
            },
            {
              title: 'Implementation & Change',
              body: 'Change management, team training, capability building, and operational integration support.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[15rem]">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <CallToActionBand
        badge="Work With Us"
        title="Start with a strategic consultation."
        description="The partner team works directly with clients on governance, infrastructure, AI readiness, and platform implementation. We can start with a focused conversation about your immediate priorities."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      />
    </>
  )
}

export function ContactPage() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitStatus('submitting')

    const form = event.currentTarget
    const formData = new FormData(form)

    try {
      const response = await fetch('https://formspree.io/f/xkopnayb', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      })

      if (response.ok) {
        setSubmitStatus('success')
        form.reset()
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      setSubmitStatus('error')
    }
  }

  return (
    <>
      <PageSection
        badge="Book a Consultation"
        title="Start with a strategic consultation."
        description="Most engagements begin with a focused conversation about governance priorities, infrastructure decisions, or readiness assessment. We help you clarify scope and determine the right path forward."
        className="pt-16"
      >
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="panel-surface p-8">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What to expect</p>
            <div className="mt-5 space-y-5">
              {[
                {
                  title: 'Initial consultation',
                  body: 'We discuss your current environment, governance priorities, and the decision or challenge in front of you.',
                },
                {
                  title: 'Scope clarification',
                  body: 'We help determine whether you need advisory services, platform implementation, or a hybrid engagement.',
                },
                {
                  title: 'Next steps',
                  body: 'If there is a fit, we outline a practical engagement path with clear deliverables and timelines.',
                },
              ].map((item) => (
                <div key={item.title}>
                  <p className="text-base font-medium text-birch">{item.title}</p>
                  <p className="mt-2 text-sm leading-6 text-weathered">{item.body}</p>
                </div>
              ))}
            </div>
          </div>

          <form className="card-panel grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
            <label className="field-label">
              <span>Name *</span>
              <input className="field-input" type="text" name="name" required />
            </label>
            <label className="field-label">
              <span>Organization *</span>
              <input className="field-input" type="text" name="organization" required />
            </label>
            <label className="field-label">
              <span>Role / Title</span>
              <input className="field-input" type="text" name="role" placeholder="e.g., Director, Chief, Manager" />
            </label>
            <label className="field-label">
              <span>Primary focus area *</span>
              <select className="field-input" name="focus" required>
                <option value="">Select a focus area</option>
                <option>OCAP Governance Consulting</option>
                <option>Infrastructure Advisory</option>
                <option>AI Readiness & Policy</option>
                <option>Vendor Risk Assessment</option>
                <option>Rez-Hub Platform Implementation</option>
                <option>General Inquiry</option>
              </select>
            </label>
            <label className="field-label">
              <span>Decision timeline</span>
              <input className="field-input" type="text" name="timeline" placeholder="e.g., Next 30 days, This quarter, Exploring" />
            </label>
            <label className="field-label">
              <span>Email *</span>
              <input className="field-input" type="email" name="email" required />
            </label>
            <label className="field-label md:col-span-2">
              <span>What brings you to Yethirihwanonhase? *</span>
              <textarea className="field-input min-h-36 resize-y" name="challenge" placeholder="Describe the governance challenge, infrastructure decision, vendor issue, or AI readiness question you're facing." required />
            </label>
            <div className="md:col-span-2">
              {submitStatus === 'success' && (
                <div className="mb-4 rounded-2xl border border-copper/30 bg-copper/10 p-4 text-sm text-birch">
                  ✓ Thank you! We'll be in touch within 1-2 business days.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-birch">
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}
              <button type="submit" className="btn-primary" disabled={submitStatus === 'submitting'}>
                {submitStatus === 'submitting' ? 'Sending...' : 'Request Strategic Consultation'}
              </button>
            </div>
          </form>
        </div>
      </PageSection>

      <PageSection
        badge="Consultation Intake"
        title="Useful context for a first conversation."
        description="The more specific you can be about your current situation, the more efficiently we can determine fit and recommend the right engagement path."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What helps us understand your needs</p>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-weathered">
              <li>Current vendors, platforms, or contracts you're evaluating</li>
              <li>Governance or stewardship issues that need operational structure</li>
              <li>AI tools, pressure, or opportunities leadership needs to assess</li>
              <li>Infrastructure decisions around hosting, access, or migration</li>
              <li>Procurement renewals or vendor dependency concerns</li>
            </ul>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Who we typically work with</p>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-weathered">
              <li>First Nations leadership and administration teams</li>
              <li>Indigenous organizations and service providers</li>
              <li>Public sector agencies working in Indigenous contexts</li>
              <li>Enterprise partners in high-trust Indigenous relationships</li>
            </ul>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Confidentiality"
        title="High-trust work requires discretion."
        description="We understand that not every conversation becomes a case study. Consultations are confidential, and we work within relationship protocols appropriate to your context."
      />
    </>
  )
}

export function ResourcesPage({ onNavigate }: PageProps) {
  return (
    <>
      <PageSection
        badge="Resources"
        title="Education-led assets for high-trust digital decisions."
        description="These resources help leadership teams frame security, governance, and AI readiness conversations internally before engagement."
        className="pt-16"
      >
        <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <SiteVisual asset={imageAssets.resources} aspectClassName="aspect-[4/3]" />
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: 'Security Brief',
                body: 'Practical framing for leaders evaluating digital control, privacy, and trust.',
                audience: 'Leadership and operations',
              },
              {
                title: 'OCAP In Practice',
                body: 'A plain-language guide to moving from policy awareness to operational implementation.',
                audience: 'Governance and stewardship teams',
              },
              {
                title: 'AI Readiness Primer',
                body: 'A governance-first framework for evaluating AI tools and deployment decisions.',
                audience: 'Leadership and innovation teams',
              },
            ].map((item) => (
              <div key={item.title} className="card-panel min-h-[16rem]">
                <p className="text-sm uppercase tracking-[0.22em] text-copper-light">{item.title}</p>
                <p className="mt-4 text-base leading-7 text-weathered">{item.body}</p>
                <p className="mt-6 text-xs uppercase tracking-[0.2em] text-weathered/80">
                  Best for: {item.audience}
                </p>
              </div>
            ))}
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Use case"
        title="Built to support internal alignment, not just marketing intake."
        description="In many organizations, one person sees the issue first but several people need to understand it before action happens. These materials should support that internal process."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">How resources are used</p>
            <div className="mt-6 space-y-5">
              {[
                'Brief leadership before a consultation.',
                'Frame a vendor or platform review internally.',
                'Clarify what AI readiness should include before tool evaluation starts.',
                'Create common language across governance, operations, and leadership roles.',
              ].map((item) => (
                <p key={item} className="text-sm leading-6 text-weathered">
                  {item}
                </p>
              ))}
            </div>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What should be added next</p>
            <div className="mt-6 space-y-5">
              {[
                'An OCAP implementation checklist',
                'A vendor assessment worksheet',
                'A leadership briefing on AI governance risk',
                'An anonymized case-study format for high-trust engagements',
              ].map((item) => (
                <p key={item} className="text-sm leading-6 text-weathered">
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </PageSection>

      <CallToActionBand
        badge="Get Started"
        title="Need a tailored conversation instead of a generic download?"
        description="If the issue is already live, we can start with a strategic consultation and point you to the most useful path."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      />
    </>
  )
}

export function SecurityTrustPage({ onNavigate }: PageProps) {
  return (
    <>
      <PageSection
        badge="Security & Trust"
        title="Trust needs to be visible in the operating model, not implied in the branding."
        description="Organizations evaluating Rez-Hub need a practical understanding of how governance, privacy, access, and risk are treated across the engagement and the product environment."
        className="pt-16"
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What this page is for</p>
            <div className="mt-6 space-y-5 text-sm leading-6 text-weathered">
              <p>Give leadership a clearer view of how trust is handled in practice.</p>
              <p>Support internal stakeholder review before consultation or procurement.</p>
              <p>Show that governance, privacy, and operational control are built into the approach from the outset.</p>
            </div>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">What trust means here</p>
            <div className="mt-6 space-y-5">
              {[
                'Clearer visibility into systems, access, and dependencies.',
                'Governance decisions reflected in implementation choices.',
                'More deliberate handling of privacy, stewardship, and risk.',
                'A delivery model suited to high-trust institutions rather than commodity deployment.',
              ].map((item) => (
                <p key={item} className="text-sm leading-6 text-weathered">
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection
        badge="Core trust areas"
        title="The areas institutional buyers typically need clarified"
        description="This page does not replace a technical review. It establishes the operating logic that sits underneath a more detailed conversation."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[
            {
              title: 'Governance alignment',
              body: 'Policy, stewardship, and operating controls are treated as connected requirements.',
            },
            {
              title: 'Access and identity',
              body: 'Role-based access and clearer user boundaries support accountability and internal control.',
            },
            {
              title: 'Vendor and dependency visibility',
              body: 'External platforms and lock-in risks are reviewed rather than assumed acceptable.',
            },
            {
              title: 'AI and data-use guardrails',
              body: 'AI readiness is framed through governance, policy, and risk before deployment pressure takes over.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[15rem]">
              <p className="text-lg font-medium text-birch">{item.title}</p>
              <p className="mt-4 text-sm leading-6 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Review path"
        title="How organizations usually evaluate trust"
        description="Buyers rarely start with a full implementation decision. They usually need enough confidence to move from exploration to serious review."
      >
        <div className="grid gap-4 md:grid-cols-4">
          {[
            'Review a brief or resource.',
            'Clarify governance and operating concerns.',
            'Assess readiness, risk, or platform fit.',
            'Move into scoped consultation or implementation planning.',
          ].map((item, index) => (
            <div key={item} className="card-panel">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Step {index + 1}</p>
              <p className="mt-4 text-base leading-7 text-birch">{item}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <CallToActionBand
        badge="Next step"
        title="Need a trust and readiness conversation?"
        description="If your team is evaluating governance fit, privacy posture, vendor exposure, or AI risk, start with a focused consultation."
        actions={
          <>
            <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
              Book a Strategic Consultation
            </RouteLink>
            <RouteLink href="/resources" onNavigate={onNavigate} className="btn-secondary">
              Explore Resources
            </RouteLink>
          </>
        }
      />
    </>
  )
}

export function CaseStudiesPage({ onNavigate }: PageProps) {
  return (
    <>
      <PageSection
        badge="Case Studies"
        title="Practical work in high-trust digital environments."
        description="Until public case studies are fully developed, this page should use anonymized institutional scenarios that demonstrate approach, decision logic, and operational outcomes."
        className="pt-16"
      >
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: 'Governance-first modernization',
              body: 'An organization needed to modernize collaboration and operations without letting governance drift behind technology decisions.',
            },
            {
              title: 'Vendor and dependency review',
              body: 'A leadership team needed clearer visibility into lock-in risk, data flow, and procurement exposure before renewal decisions.',
            },
            {
              title: 'AI readiness before adoption',
              body: 'A team needed to assess AI pressure, policy gaps, and acceptable use boundaries before tools became normalized internally.',
            },
          ].map((item) => (
            <div key={item.title} className="card-panel min-h-[17rem]">
              <p className="text-sm uppercase tracking-[0.22em] text-copper-light">{item.title}</p>
              <p className="mt-4 text-base leading-7 text-weathered">{item.body}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="Scenario structure"
        title="Each case study should answer the same practical questions"
        description="The objective is not storytelling for its own sake. It is helping buyers understand what changes operationally after engagement."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
          {[
            'Client context',
            'Challenge',
            'Governance or infrastructure issue',
            'Approach',
            'Operational outcome',
          ].map((item) => (
            <div key={item} className="card-panel">
              <p className="text-base leading-7 text-birch">{item}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection
        badge="What to avoid"
        title="Weak proof is worse than no proof."
        description="This page should stay disciplined. Avoid inflated metrics, anonymous claims with no operational substance, or vague transformation language."
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="panel-surface p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Use instead</p>
            <div className="mt-6 space-y-4 text-sm leading-6 text-weathered">
              <p>Decision context</p>
              <p>Governance issue</p>
              <p>Risk addressed</p>
              <p>What changed in practice</p>
            </div>
          </div>
          <div className="card-panel">
            <p className="text-sm uppercase tracking-[0.22em] text-copper-light">Avoid</p>
            <div className="mt-6 space-y-4 text-sm leading-6 text-weathered">
              <p>Inflated claims with no substantiation</p>
              <p>Unclear before-and-after language</p>
              <p>Generic consulting phrasing</p>
              <p>Metrics that cannot be defended publicly</p>
            </div>
          </div>
        </div>
      </PageSection>

      <CallToActionBand
        badge="Get Started"
        title="Need a scenario closer to your environment?"
        description="If a public case study is not yet available for your context, we can start with a consultation and frame the relevant operating questions directly."
        actions={
          <RouteLink href="/contact" onNavigate={onNavigate} className="btn-primary">
            Book a Strategic Consultation
          </RouteLink>
        }
      />
    </>
  )
}
